import { FC } from 'react';
import { Stack } from '@mui/material';
import { AutocompleteInput, Modal } from 'melp-design/components';
import { Controller, SubmitHandler, useForm } from 'react-hook-form-latest';
import { useTranslation } from 'react-i18next';
import { ModalProps } from 'store/modal';
import { usePredefinedToasts } from 'utils/Toast';
import { shopItemCategories, ShopItemCategory } from 'types/MarketplaceItems';
import { useUpdateShopItem } from 'store/admin-shop';

interface Values {
  categories: ShopItemCategory[];
}

interface Props extends ModalProps {
  shopItemId: string;
  initialValues: Values;
}

export const UpdateShopItemCategoryModal: FC<Props> = ({
  shopItemId,
  initialValues,
  closeModal,
}) => {
  const { t } = useTranslation();
  const predefinedToasts = usePredefinedToasts();

  const { handleSubmit, control } = useForm<Values>({
    defaultValues: initialValues,
  });

  const { mutate: updateShopItem, isLoading } = useUpdateShopItem();

  const onSubmit: SubmitHandler<Values> = (values) => {
    updateShopItem(
      { shopItemId, item: { categories: values.categories } },
      {
        onSuccess: () => {
          predefinedToasts.success.updated();
          closeModal();
        },
      },
    );
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Modal.Content
        title={t('common.edit')}
        actions={[
          {
            variant: 'neutral-outline',
            onClick: () => closeModal(),
            label: t('common.cancel'),
          },
          {
            variant: 'primary',
            label: t('common.save'),
            type: 'submit',
            disabled: isLoading,
          },
        ]}
      >
        <Stack gap="20px">
          <Controller
            name="categories"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => (
              <AutocompleteInput
                label={t('marketplaceItems.categories')}
                name={name}
                value={value}
                onChange={onChange}
                options={shopItemCategories.map((category) => ({
                  label: t(`marketplaceItems.itemCategory-${category}`),
                  value: category,
                }))}
                multiple
                required
                ref={ref}
              />
            )}
          />
        </Stack>
      </Modal.Content>
    </form>
  );
};
